import { useTranslation } from 'react-i18next';

import { botarPresupuesto } from '#budgetfiles/budgetfilesSlice';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import type { DatosBarco } from './Astillero';
import { guardarPortada } from './portada';

/**
 * Pref `travesia` de un barco recién botado: lo mismo que `travesiaNueva()`
 * de `mobile/travesia/estado.ts`, que no se puede importar desde aquí (los
 * `.ts` de otra carpeta necesitan su propio alias en `package.json#imports`).
 */
export const TRAVESIA_NUEVA = JSON.stringify({ estado: 'pendiente', paso: 2 });

/** Igual, pero sin Gavi: la app aplica «Saltar» en silencio al abrirlo. */
export const TRAVESIA_SIN_GAVI = JSON.stringify({
  estado: 'pendiente',
  paso: 2,
  silencio: true,
});

/**
 * Lo que hace el astillero al zarpar: crea el presupuesto (en este móvil y en
 * la nube), le pone los colores elegidos y lo abre. Si falla, o si no había
 * conexión para subirlo, lo avisa. Lo usan el
 * puerto y la pantalla de bienvenida.
 *
 * Todo barco botado aquí deja la «Primera travesía» pendiente en el paso 2
 * (o, si se eligió zarpar sin Gavi, para saltarla en silencio)
 * (pref sincronizada `travesia`): el anfitrión la retoma con el presupuesto
 * ya abierto. La pref y la limpieza de las categorías de fábrica las escribe
 * el worker DENTRO del archivo, antes de subirlo (`create-budget` con
 * `arranque`): si iOS corta la página justo después, o se pasa de Safari a
 * la PWA, el barco que baje ya va en castellano y con Gavi. Importar de
 * YNAB, la demo, duplicar o abrir un barco compartido no pasan por aquí, así
 * que nunca la lanzan.
 */
export function useBotarBarco() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const onBotar = async ({ nombre, portadaId, conGavi = true }: DatosBarco) => {
    const avisarFallo = () =>
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t(
              'The boat could not be launched. Try again in a moment.',
            ),
          },
        }),
      );
    // Si algo lanza (p. ej. `loadAllFiles` tras crear), también se avisa.
    const r = await dispatch(
      botarPresupuesto({
        budgetName: nombre || undefined,
        arranque: {
          prefs: { travesia: conGavi ? TRAVESIA_NUEVA : TRAVESIA_SIN_GAVI },
          ingresos: t('Income'),
        },
      }),
    )
      .unwrap()
      .catch(() => null);
    if (!r || ('error' in r && r.error)) {
      avisarFallo();
      return;
    }
    const creado = r as { id?: string; cloudFileId?: string | null };
    const clave = creado.cloudFileId || creado.id;
    if (clave) {
      guardarPortada(clave, { tipo: 'predefinida', id: portadaId });
    }
    // `botarPresupuesto` ya deja el barco abierto y con sus prefs cargadas
    // (la `travesia` incluida: la escribió el worker).
    if (!creado.cloudFileId) {
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t(
              'No connection: for now it is on this phone. It will go up to the cloud by itself as soon as the connection is back.',
            ),
          },
        }),
      );
    }
  };

  return { onBotar };
}
